import type { GatsbyBrowser } from "gatsby";
import { PRODUCT } from "./src/constants";

type GtagArguments = IArguments;
type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: IArguments[];
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    dataLayer: GtagArguments[];
    gtag: (...args: unknown[]) => void;
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const analyticsId = PRODUCT.analyticsId;
const metaPixelId = PRODUCT.metaPixelId;
let loadScheduled = false;

function initializeQueue() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
}

function loadAnalytics() {
  if (document.querySelector(`script[data-knocky-analytics]`)) return;

  const script = document.createElement("script");
  script.async = true;
  script.dataset.knockyAnalytics = "true";
  script.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`;
  document.head.appendChild(script);

  if (metaPixelId && !document.querySelector(`script[data-knocky-meta-pixel]`)) {
    const pixel = document.createElement("script");
    pixel.async = true;
    pixel.dataset.knockyMetaPixel = "true";
    pixel.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(pixel);
  }
}

function initializeMeta() {
  if (!metaPixelId || window.fbq) return;
  let fbq: Fbq;
  fbq = function queueMetaEvent(...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
      return;
    }
    fbq.queue = fbq.queue || [];
    fbq.queue.push(arguments);
  } as Fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = window._fbq = fbq;
  fbq("init", metaPixelId);
}

const campaignParameters = ["utm_source", "utm_medium", "utm_campaign", "utm_id", "utm_content", "utm_term", "fbclid"];

function decorateAcquisitionLinks() {
  const source = new URLSearchParams(window.location.search);
  const values = campaignParameters.flatMap(name => source.has(name) ? [[name, source.get(name)!] as const] : []);
  if (!values.length) return;
  document.querySelectorAll<HTMLAnchorElement>("a[data-acquisition-link]").forEach(link => {
    const target = new URL(link.href);
    for (const [name, value] of values) target.searchParams.set(name, value.slice(0, 500));
    link.href = target.toString();
  });
}

function scheduleAnalytics() {
  if (loadScheduled) return;
  loadScheduled = true;

  const runWhenIdle = () => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(loadAnalytics, { timeout: 2_000 });
      return;
    }
    window.setTimeout(loadAnalytics, 0);
  };

  runWhenIdle();
}

export const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  initializeQueue();
  initializeMeta();
  window.gtag("js", new Date());
  window.gtag("config", analyticsId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  decorateAcquisitionLinks();
  scheduleAnalytics();
  document.addEventListener("click", event => {
    const link = (event.target as Element | null)?.closest<HTMLAnchorElement>("a[data-acquisition-action]");
    if (!link) return;
    const action = link.dataset.acquisitionAction;
    const mouseEvent = event as MouseEvent;
    const shouldDelayNavigation = mouseEvent.button === 0
      && !mouseEvent.metaKey
      && !mouseEvent.ctrlKey
      && !mouseEvent.shiftKey
      && !mouseEvent.altKey
      && link.target !== "_blank";
    let navigated = false;
    const navigate = () => {
      if (!shouldDelayNavigation || navigated) return;
      navigated = true;
      window.location.assign(link.href);
    };
    if (shouldDelayNavigation) event.preventDefault();
    window.gtag("event", "sign_up_start", {
      method: "Google",
      source: "home",
      action,
      transport_type: "beacon",
      event_callback: navigate,
      event_timeout: 250,
    });
    window.fbq?.("trackCustom", "SignUpStart", { source: "home", action });
    if (shouldDelayNavigation) window.setTimeout(navigate, 250);
  });
};

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({ location }) => {
  initializeQueue();
  initializeMeta();
  decorateAcquisitionLinks();
  window.gtag("event", "page_view", {
    page_title: document.title,
    page_location: location.href,
    page_path: `${location.pathname}${location.search}`,
  });
  window.fbq?.("track", "PageView");
};
